import React, { JSX, useMemo, useEffect } from "react";
import {
  Link as RouterLink,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import LoginPage from "./pages/LoginPage";
import DataPage from "./pages/DataPage";
import CodelistsPage from "./pages/CodelistsPage";
import UsersPage from "./pages/UsersPage";
import ChatPage from "./pages/ChatPage";
import { useAuth } from "./auth/AuthContext";
import { decodeRoles, getHighestRole } from "./auth/AuthUtils";
import { setGetTokenFn } from "./api/http";
import {
  AppBar,
  Box,
  Button,
  Container,
  Link,
  Toolbar,
  Typography,
} from "@mui/material";

function RequireAuth({ children }: { children: JSX.Element }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return children;
}

function RequireAdmin({ children }: { children: JSX.Element }) {
  const { token } = useAuth();
  const role = getHighestRole(decodeRoles(token));

  if (role !== "ADMIN" && role !== "SUPERADMIN") {
    return <Navigate to="/data" replace />;
  }
  return children;
}

export default function App() {
  const { logout, isAuthenticated, token } = useAuth();
  const role = useMemo(() => getHighestRole(decodeRoles(token)), [token]);
  const isAdminOrSuper = role === "ADMIN" || role === "SUPERADMIN";

  useEffect(() => {
    setGetTokenFn(() => token);
  }, [token]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <AppBar position="fixed">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Anavela
          </Typography>
          {isAuthenticated && (
            <>
              <Link
                component={RouterLink}
                to="/data"
                color="inherit"
                underline="none"
                sx={{ mr: 2 }}
              >
                Data
              </Link>
              {isAdminOrSuper && (
                <>
                  <Link
                    component={RouterLink}
                    to="/chat"
                    color="inherit"
                    underline="none"
                    sx={{ mr: 2 }}
                  >
                    Chat
                  </Link>
                  <Link
                    component={RouterLink}
                    to="/codelists"
                    color="inherit"
                    underline="none"
                    sx={{ mr: 2 }}
                  >
                    Codelists
                  </Link>
                  <Link
                    component={RouterLink}
                    to="/users"
                    color="inherit"
                    underline="none"
                    sx={{ mr: 2 }}
                  >
                    Users
                  </Link>
                </>
              )}
              <Button color="inherit" onClick={logout}>
                Logout
              </Button>
            </>
          )}
        </Toolbar>
      </AppBar>
      <Toolbar />
      <Container maxWidth={false} sx={{ width: "95%", flex: 1, py: 2 }}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/data"
            element={
              <RequireAuth>
                <DataPage />
              </RequireAuth>
            }
          />
          <Route
            path="/codelists"
            element={
              <RequireAuth>
                <RequireAdmin>
                  <CodelistsPage />
                </RequireAdmin>
              </RequireAuth>
            }
          />
          <Route
            path="/users"
            element={
              <RequireAuth>
                <UsersPage />
              </RequireAuth>
            }
          />
          <Route
            path="/chat"
            element={
              <RequireAuth>
                <ChatPage />
              </RequireAuth>
            }
          />
          <Route path="*" element={<Navigate to="/data" replace />} />
        </Routes>
      </Container>
    </Box>
  );
}
